import { createFileRoute, useLocation } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { match } from 'ts-pattern';

import { HStack, VStack } from '@/components/Layout';
import { WorkerType } from '@/constants/workerType';
import Processing from '@/features/import-csv/-components/Processing';
import PreviewFile from '@/features/import-csv/-features/PreviewFile';
import { ImportCsvSearchSchema } from '@/features/import-csv/zod-schemas';
import { defaultNS, useTranslation } from '@/lib/i18n';

import AddStepperNavigation from './-component/AddStepperNavigation';
import AddStepperStepTitleCard from './-component/AddStepperStepTitleCard';
import {
  EXTERNAL_HR_SAAS_PREVIEW_HASH,
  type EXTERNAL_HR_SAAS_PREVIEW_HASH_VALUE,
} from './-constants/hash';
import useTableRows from './-mutations/useTableRows';
import useGetExternalHrSaasAppIntegrationSyncPreview from './-queries/useGetExternalHrSaasAppIntegrationSyncPreview';
import useGetExternalHrSaasAppIntegrationSyncStatus from './-queries/useGetExternalHrSaasAppIntegrationSyncStatus';

export const Route = createFileRoute(
  '/_staff/person/external-hr-saas/$appIntegrationId/preview/',
)({
  component: RouteComponent,
  validateSearch: zodValidator(ImportCsvSearchSchema),
});

function RouteComponent() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const location = useLocation();

  const { appIntegrationId } = Route.useParams();

  const hash = location.hash
    ? (location.hash.replace('#', '') as EXTERNAL_HR_SAAS_PREVIEW_HASH_VALUE)
    : EXTERNAL_HR_SAAS_PREVIEW_HASH.EMPLOYEE;

  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.person.importCsv.workerType',
  });

  const { t: tPreview } = useTranslation(defaultNS, {
    keyPrefix: 'pages.person.externalHrSaas.preview',
  });

  const workerType = match(hash)
    .with(EXTERNAL_HR_SAAS_PREVIEW_HASH.EMPLOYEE, () => WorkerType.EMPLOYEE)
    .with(EXTERNAL_HR_SAAS_PREVIEW_HASH.CONTRACTOR, () => WorkerType.CONTRACTOR)
    .with(
      EXTERNAL_HR_SAAS_PREVIEW_HASH.BOARD_MEMBER,
      () => WorkerType.BOARD_MEMBER,
    )
    .with(
      EXTERNAL_HR_SAAS_PREVIEW_HASH.COLLABORATOR,
      () => WorkerType.COLLABORATOR,
    )
    .exhaustive();

  const unit = match(workerType)
    .with(WorkerType.EMPLOYEE, () => t('unit.employees'))
    .with(WorkerType.CONTRACTOR, () => t('unit.contractors'))
    .with(WorkerType.BOARD_MEMBER, () => t('unit.boardMembers'))
    .with(WorkerType.COLLABORATOR, () => t('unit.collaborators'))
    .exhaustive();

  const { mutateTableRows, filterError, filterWarning } = useTableRows();

  const { data, isPending } = useGetExternalHrSaasAppIntegrationSyncPreview({
    appIntegrationId,
    workerType,
    filterError,
    filterWarning,
  });

  const { data: syncStatus } = useGetExternalHrSaasAppIntegrationSyncStatus({
    appIntegrationId,
    workerType,
  });

  return (
    <HStack className="h-[calc(100vh-64px)] w-full items-start gap-9 p-10">
      <AddStepperNavigation
        hash={hash}
        appIntegrationId={appIntegrationId}
        syncStatus={syncStatus}
      />

      <VStack className="h-full min-w-0 flex-1 items-start gap-6 p-0">
        <AddStepperStepTitleCard hash={hash} />

        {isPending && <Processing hash={hash} />}

        {!isPending && (
          <PreviewFile
            hash={hash}
            unit={unit}
            search={search}
            navigate={navigate}
            tableColumnDefs={data.tableColumnDefs}
            tableRows={data}
            mutateTableRows={mutateTableRows}
            filterError={filterError}
            filterWarning={filterWarning}
            summaryErrorMessage={tPreview('previewFile.summaryErrorMessage')}
            summaryWarningMessage={tPreview(
              'previewFile.summaryWarningMessage',
            )}
          />
        )}
      </VStack>
    </HStack>
  );
}
